import { appApiPath, buildSharePageUrl } from '@/lib/share';
import { cn } from '@/lib/utils';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
  ChainlitContext,
  ClientError,
  IThread,
  ThreadHistory,
  threadHistoryState,
  useChatInteract,
  useChatMessages,
  useChatSession
} from '@chainlit/react-client';

import Alert from '@/components/Alert';
import { Loader } from '@/components/Loader';
import MaterialIcon from '@/components/MaterialIcon';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { Translator } from '../i18n';
import ThreadOptions from './ThreadOptions';

interface ThreadListProps {
  threadHistory?: ThreadHistory;
  error?: string;
  isFetching: boolean;
  isLoadingMore: boolean;
}

export function ThreadList({
  threadHistory,
  error,
  isFetching,
  isLoadingMore
}: ThreadListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { idToResume } = useChatSession();
  const { clear } = useChatInteract();
  const { threadId: currentThreadId } = useChatMessages();
  const [threadIdToDelete, setThreadIdToDelete] = useState<string>();
  const [threadIdToRename, setThreadIdToRename] = useState<string>();
  const [threadNewName, setThreadNewName] = useState<string>();
  const [shareThreadId, setShareThreadId] = useState<string>();
  const [shareUrl, setShareUrl] = useState<string>();
  const [shareLoading, setShareLoading] = useState(false);
  const [areChatsExpanded, setAreChatsExpanded] = useState(false);
  const setThreadHistory = useSetRecoilState(threadHistoryState);
  const apiClient = useContext(ChainlitContext);

  const threads = threadHistory?.threads || [];
  const pinnedThreads = useMemo(
    () => threads.filter((thread) => Boolean(thread.metadata?.pinned)),
    [threads]
  );
  const unpinnedThreads = useMemo(
    () => threads.filter((thread) => !thread.metadata?.pinned),
    [threads]
  );
  const activeThreadId = threadHistory?.currentThreadId || currentThreadId;
  const visibleThreads = areChatsExpanded
    ? unpinnedThreads
    : unpinnedThreads.filter((thread) => thread.id === activeThreadId);

  if (isFetching || (!threadHistory?.timeGroupedThreads && isLoadingMore)) {
    return (
      <div className="flex items-center justify-center p-2">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error" className="m-3">
        {error}
      </Alert>
    );
  }

  const handleDeleteThread = async () => {
    if (!threadIdToDelete) return;
    if (
      threadIdToDelete === idToResume ||
      threadIdToDelete === currentThreadId
    ) {
      clear();
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    // Revoke any public link before removing the thread. The backend deletion
    // also clears it, so this is intentionally best-effort.
    try {
      await fetch(
        appApiPath(`/api/share/thread/${encodeURIComponent(threadIdToDelete)}`),
        {
          method: 'DELETE',
          credentials: 'same-origin'
        }
      );
    } catch {
      // The thread deletion below remains authoritative.
    }

    toast.promise(apiClient.deleteThread(threadIdToDelete), {
      loading: (
        <Translator path="threadHistory.thread.actions.delete.inProgress" />
      ),
      success: () => {
        setThreadHistory((prev) => ({
          ...prev,
          threads: prev?.threads?.filter((t) => t.id !== threadIdToDelete)
        }));
        navigate('/');
        return (
          <Translator path="threadHistory.thread.actions.delete.success" />
        );
      },
      error: (err) => {
        if (err instanceof ClientError) {
          return <span>{err.message}</span>;
        } else {
          return <span></span>;
        }
      }
    });
  };

  const handleRenameThread = () => {
    if (!threadIdToRename || !threadNewName) return;

    toast.promise(apiClient.renameThread(threadIdToRename, threadNewName), {
      loading: (
        <Translator path="threadHistory.thread.actions.rename.inProgress" />
      ),
      success: () => {
        setThreadNewName(undefined);
        setThreadIdToRename(undefined);
        setThreadHistory((prev) => {
          const next = {
            ...prev,
            threads: prev?.threads ? [...prev.threads] : undefined
          };
          const threadIndex = next.threads?.findIndex(
            (t) => t.id === threadIdToRename
          );
          if (typeof threadIndex === 'number' && next.threads) {
            next.threads[threadIndex] = {
              ...next.threads[threadIndex],
              name: threadNewName
            };
          }
          return next;
        });
        return (
          <div>
            <Translator path="threadHistory.thread.actions.rename.success" />
          </div>
        );
      },
      error: (err) => {
        if (err instanceof ClientError) {
          return <span>{err.message}</span>;
        } else {
          return <span></span>;
        }
      }
    });
  };

  const openShareDialog = async (threadId: string) => {
    setShareThreadId(threadId);
    setShareUrl(undefined);
    setShareLoading(true);
    try {
      const response = await fetch(
        appApiPath(`/api/share/thread/${encodeURIComponent(threadId)}`),
        { credentials: 'same-origin' }
      );
      if (!response.ok) throw new Error('Failed to load share status');

      const data = await response.json();
      setShareUrl(
        data.shared && data.token
          ? buildSharePageUrl(data.token as string)
          : undefined
      );
    } catch (error: any) {
      toast.error(error?.message || 'Failed to open share dialog');
      setShareThreadId(undefined);
    } finally {
      setShareLoading(false);
    }
  };

  const createShareLink = async () => {
    if (!shareThreadId) return;
    setShareLoading(true);
    try {
      const response = await fetch(appApiPath('/api/share/thread'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ threadId: shareThreadId })
      });
      if (!response.ok) {
        throw new Error(
          (await response.text()) ||
            t('threadHistory.thread.actions.share.errors.create',
              'Failed to create share link')
        );
      }

      const data = await response.json();
      const url = buildSharePageUrl(data.token as string);
      setShareUrl(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success(
          t('threadHistory.thread.actions.share.createdCopied',
            'Share link created and copied')
        );
      } catch {
        toast.success(
          t('threadHistory.thread.actions.share.created', 'Share link created')
        );
      }
    } catch (error: any) {
      toast.error(
        error?.message ||
          t('threadHistory.thread.actions.share.errors.create',
            'Failed to create share link')
      );
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(
        t('threadHistory.thread.actions.share.copied', 'Share link copied')
      );
    } catch {
      toast.error(
        t('threadHistory.thread.actions.share.copyFailed', 'Could not copy link')
      );
    }
  };

  const stopSharing = async () => {
    if (!shareThreadId) return;
    setShareLoading(true);
    try {
      const response = await fetch(
        appApiPath(`/api/share/thread/${encodeURIComponent(shareThreadId)}`),
        { method: 'DELETE', credentials: 'same-origin' }
      );
      if (!response.ok)
        throw new Error(
          t('threadHistory.thread.actions.share.errors.stop',
            'Failed to stop sharing')
        );
      setShareUrl(undefined);
      toast.success(
        t('threadHistory.thread.actions.share.stopped', 'Sharing stopped')
      );
    } catch (error: any) {
      toast.error(
        error?.message ||
          t('threadHistory.thread.actions.share.errors.stop',
            'Failed to stop sharing')
      );
    } finally {
      setShareLoading(false);
    }
  };

  const togglePin = async (thread: IThread) => {
    const pinned = !thread.metadata?.pinned;
    try {
      const response = await fetch(appApiPath('/project/thread/pin'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ threadId: thread.id, pinned })
      });
      if (!response.ok) {
        throw new Error(
          (await response.text()) ||
            t('threadHistory.thread.actions.pin.error', 'Failed to update pin')
        );
      }

      setThreadHistory((prev) => ({
        ...prev,
        threads: prev?.threads?.map((item) =>
          item.id !== thread.id
            ? item
            : (() => {
                const { pinned: _pinned, ...metadata } = item.metadata || {};
                return {
                  ...item,
                  metadata: pinned ? { ...metadata, pinned: true } : metadata
                };
              })()
        )
      }));
      toast.success(
        pinned
          ? t('threadHistory.thread.actions.pin.pinned', 'Chat pinned')
          : t('threadHistory.thread.actions.pin.unpinned', 'Chat unpinned')
      );
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update pin');
    }
  };

  const renderThread = (thread: IThread) => {
    const isResumed =
      idToResume === thread.id && !threadHistory?.currentThreadId;
    const isSelected = isResumed || activeThreadId === thread.id;

    return (
      <Link
        key={thread.id}
        id={`thread-${thread.id}`}
        to={isResumed ? '' : `/thread/${thread.id}`}
        className="relative"
      >
        <div
          className={cn(
            'fongpt-hover fongpt-thread-row group/thread flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2 text-left text-secondary',
            isSelected && 'bg-white/[0.07] text-primary'
          )}
        >
          <MaterialIcon
            name="chat_bubble"
            className="text-[18px] flex-shrink-0 opacity-70"
          />
          <span className="flex-1 truncate text-[13.5px] font-medium">
            {thread.name || <Translator path="threadHistory.thread.untitled" />}
          </span>
          <ThreadOptions
            onDelete={() => setThreadIdToDelete(thread.id)}
            onRename={() => {
              setThreadIdToRename(thread.id);
              setThreadNewName(thread.name);
            }}
            onShare={() => openShareDialog(thread.id)}
            onTogglePin={() => togglePin(thread)}
            isPinned={Boolean(thread.metadata?.pinned)}
            className="fongpt-hover flex h-8 w-8 text-secondary"
          />
        </div>
      </Link>
    );
  };

  return (
    <>
      <AlertDialog
        open={!!threadIdToDelete}
        onOpenChange={() => setThreadIdToDelete(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Translator path="threadHistory.thread.actions.delete.title" />
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Translator path="threadHistory.thread.actions.delete.description" />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-2 sm:gap-0">
            <AlertDialogCancel className="mt-0">
              <Translator path="common.actions.cancel" />
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteThread}>
              <Translator path="common.actions.confirm" />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog
        open={!!threadIdToRename}
        onOpenChange={() => setThreadIdToRename(undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Translator path="threadHistory.thread.actions.rename.title" />
            </DialogTitle>
            <DialogDescription>
              <Translator path="threadHistory.thread.actions.rename.description" />
            </DialogDescription>
          </DialogHeader>
          <div className="my-6">
            <Label htmlFor="name" className="text-right">
              <Translator path="threadHistory.thread.actions.rename.form.name.label" />
            </Label>
            <Input
              id="name"
              required
              value={threadNewName}
              onChange={(e) => setThreadNewName(e.target.value)}
              placeholder={t(
                'threadHistory.thread.actions.rename.form.name.placeholder'
              )}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setThreadIdToRename(undefined)}
            >
              <Translator path="common.actions.cancel" />
            </Button>
            <Button type="button" onClick={handleRenameThread}>
              <Translator path="common.actions.confirm" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!shareThreadId}
        onOpenChange={(open) => {
          if (!open) {
            setShareThreadId(undefined);
            setShareUrl(undefined);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('threadHistory.thread.actions.share.title')}
            </DialogTitle>
            <DialogDescription>
              {t('threadHistory.thread.actions.share.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="my-4 space-y-2">
            <Label htmlFor="share-url">
              {t('threadHistory.thread.actions.share.linkLabel')}
            </Label>
            <Input
              id="share-url"
              readOnly
              value={
                shareLoading
                  ? t('threadHistory.thread.actions.share.loading')
                  : shareUrl || t('threadHistory.thread.actions.share.empty')
              }
              onFocus={(event) => shareUrl && event.target.select()}
            />
          </div>
          <DialogFooter className="flex-row flex-wrap gap-2 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              disabled={shareLoading || !shareUrl}
              onClick={stopSharing}
              className="text-red-500"
            >
              {t('threadHistory.thread.actions.share.stop')}
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShareThreadId(undefined);
                  setShareUrl(undefined);
                }}
              >
                {t('threadHistory.thread.actions.share.close')}
              </Button>
              {shareUrl ? (
                <Button
                  type="button"
                  disabled={shareLoading}
                  onClick={copyShareUrl}
                >
                  {t('threadHistory.thread.actions.share.copy')}
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={shareLoading || !shareThreadId}
                  onClick={createShareLink}
                >
                  {t('threadHistory.thread.actions.share.create')}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <section className="mb-2" aria-labelledby="pinned-chats-heading">
        <p
          id="pinned-chats-heading"
          className="px-3 py-1.5 text-[11px] tracking-wide text-muted"
        >
          {t('threadHistory.sidebar.sections.pinned', 'Pinned')}
        </p>
        {pinnedThreads.length ? (
          <div className="flex flex-col">{pinnedThreads.map(renderThread)}</div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 text-[12px] text-muted">
            <MaterialIcon name="push_pin" className="text-[16px]" />
            <span>
              {t(
                'threadHistory.sidebar.sections.pinnedEmpty',
                'Pin chats to keep them here'
              )}
            </span>
          </div>
        )}
      </section>
      <section aria-labelledby="chats-heading">
        <button
          id="chats-heading"
          type="button"
          onClick={() => setAreChatsExpanded((expanded) => !expanded)}
          aria-expanded={areChatsExpanded}
          className="fongpt-hover flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-left text-[11px] tracking-wide text-muted"
        >
          <span>{t('threadHistory.sidebar.sections.chats', 'Chats')}</span>
          <MaterialIcon
            name={
              areChatsExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
            }
            className="text-[17px]"
          />
        </button>
        <div className="flex flex-col">{visibleThreads.map(renderThread)}</div>
      </section>
      {isLoadingMore ? (
        <div className="flex items-center justify-center p-2">
          <Loader />
        </div>
      ) : null}
    </>
  );
}
